/**
 * The money for finance (Costs › Statements, Enterprise): the month's chargeback statement per business area, reconciled
 * to the ledger, with its seats, invoices and late usage; closing a month. Moved here unchanged from the Costs page when
 * token economics rebuilt it (0.90.0).
 */

import { formatMoney, invoiceProviderWords, monthWords, type ChargebackInvoiceLine, type ChargebackLine, type ChargebackSeatLine, type ChargebackStatement } from '@aioe/shared';
import { dateLong } from '@aioe/shared/wording/dateTime';
import { ArrowDownToLine, Receipt } from '@gravity-ui/icons';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { download, routes } from '../../api/client.ts';
import { useLoad } from '../../hooks.ts';
import { useMoney } from '../../money.tsx';
import { SubjectName } from '../SubjectPicker.tsx';
import { VENDOR_WORDS } from '../pricing.tsx';
import { Button, ConfirmDialog, Empty, ErrorNote, ListBlock, ListGroup, ListRow, selectClass, StatusMark } from '../ui.tsx';
import { VIEW_TONES } from '../ui/Art.tsx';

/** Amounts in the organisation's currency; the ledger is US dollars. */
function useUsd() {
  const { format } = useMoney();
  return (n: number, digits = 2) => format(n, { digits });
}
const compact = (n: number) => n.toLocaleString(undefined, { notation: 'compact', maximumFractionDigits: 1 });
const monthKey = (d: Date) => `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;

/** The month's statement per business area, reconciled to the ledger, and its CSV; a month that is over can be closed. */
export function Chargeback() {
  const live = useUsd();
  const tone = VIEW_TONES.costs!;
  const [month, setMonth] = useState(() => monthKey(new Date()));
  const s = useLoad(() => routes.chargeback(month), [month]);
  const [busy, setBusy] = useState(false);
  const [closing, setClosing] = useState(false);
  const [error, setError] = useState<string>();
  const save = async () => {
    setBusy(true);
    setError(undefined);
    try {
      await download(routes.chargebackCsvUrl(month), `chargeback-${month}.csv`);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const close = async () => {
    setError(undefined);
    try {
      await routes.closeChargeback(month);
      setClosing(false);
      s.reload();
    } catch (e) {
      setError((e as { body?: { message?: string } }).body?.message ?? (e as Error).message);
    }
  };
  const st: ChargebackStatement | undefined = s.data;
  // A closed month shows at the rate it was closed at; an open one at today's.
  const usd = st?.closed && st.money ? (n: number, digits = 2) => formatMoney(n, st.money!, { digits }) : live;
  const over = month < monthKey(new Date());
  const late = st?.lateAdjustments ?? [];
  const seats = Boolean(st?.seats?.length);
  const nothing = st && st.total.calls === 0 && !late.length && !seats;
  return (
    <>
      <ListGroup
        title="Chargeback statement"
        description="One line per business area for the month, its projects beneath, reconciled to the ledger. Spend is metered; subscription value is what paid seats' calls would have cost, not spend."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <input type="month" className={`${selectClass} w-44`} value={month} max={monthKey(new Date())} onChange={(e) => e.target.value && (setMonth(e.target.value), setClosing(false))} aria-label="Month" />
            <Button size="sm" variant="secondary" onPress={() => void save()} isDisabled={busy || !st || nothing}>
              <ArrowDownToLine /> CSV
            </Button>
            {st && over && !st.closed && !st.areaIds && (
              <Button size="sm" variant="secondary" onPress={() => setClosing(true)}>
                Close the month
              </Button>
            )}
          </div>
        }
        empty={nothing && !(s.error ?? error) ? <Empty tone={tone} icon={<Receipt />} title={`Nothing to charge for ${monthName(month)}`} body="No workbench or node reported a model call in this month." compact /> : undefined}
      >
        {s.error || error ? (
          <ListBlock>
            <ErrorNote error={s.error ?? error} />
          </ListBlock>
        ) : null}
        {!st && !s.error ? <ListBlock className="text-sm text-muted">Reading the statement…</ListBlock> : null}
        {st ? (
          <ListRow
            title={monthName(month)}
            description={
              <>
                <span className="block">
                  <span className="tabular-nums">{usd(st.total.usd)}</span> spent across {st.lines.length} {st.lines.length === 1 ? 'line' : 'lines'}, {compact(st.total.tokens)} tokens, {compact(st.total.calls)} calls
                  {st.total.unpricedCalls ? `, ${compact(st.total.unpricedCalls)} unpriced` : ''}.
                </span>
                <span className="block">
                  {st.closed ? (
                    <>
                      Closed {dateLong(st.closedAt!)} by <SubjectName id={st.closedBy!} />
                      {st.money && st.money.currency !== 'USD' ? `, at US$1 = ${st.money.perUsd} ${st.money.currency}` : ''}. Later usage for this month goes on the next statement.
                    </>
                  ) : over ? (
                    'Open: usage that arrives late still changes it. Close it once the month is settled.'
                  ) : (
                    'This month, so far.'
                  )}
                  {st.timeZone ? ` The month runs in ${st.timeZone.replace(/_/g, ' ')} time.` : ''}
                  {st.areaIds ? ' Showing the business areas you manage.' : ''}
                </span>
              </>
            }
            status={<StatusMark state={st.reconciled ? 'connected' : 'error'} label={st.reconciled ? 'Reconciled to the ledger' : `Off the ledger by ${usd(Math.abs(st.ledger.usd - st.total.usd))}`} />}
          />
        ) : null}
        {st && st.lines.length ? <Lines lines={st.lines} usd={usd} seats={seats} /> : null}
      </ListGroup>
      {st && seats && !nothing ? <SeatLines seats={st.seats!} total={st.seatUsd ?? 0} usd={usd} /> : null}
      {st?.invoices?.length && !nothing ? <InvoiceLines invoices={st.invoices} usd={usd} /> : null}
      {!nothing
        ? late.map((a) => (
            <ListGroup key={a.month} title={`Late usage for ${monthName(a.month)}`} description={`${usd(a.total.usd)} arrived after ${monthName(a.month)} was closed; not in the total above.`}>
              <Lines lines={a.lines} usd={usd} seats={false} />
            </ListGroup>
          ))
        : null}
      <ConfirmDialog isOpen={closing && Boolean(st)} title={`Close ${monthName(month)}?`} confirmLabel={`Close ${monthName(month)}`} onConfirm={close} onCancel={() => setClosing(false)}>
        <p>
          Closing {monthName(month)} freezes this statement as it stands, at today's rate. Usage for {monthName(month)} that arrives later appears on the next month's statement as a late adjustment.
        </p>
      </ConfirmDialog>
    </>
  );
}

/**
 * Business areas with their projects beneath, as a table flush in its group: each with its cost code (a
 * project's own beats its area's), its spend, its subscription value and its share of the seats when the
 * statement carries seats. Narrow, the subscription value and seats go under the spend.
 */
function Lines({ lines, usd, seats }: { lines: ChargebackLine[]; usd: (n: number, digits?: number) => string; seats: boolean }) {
  return (
    <ListBlock flush>
      <div className="data-table-scroll">
        <table className="data-table">
          <thead>
            <tr>
              <th>Business area and projects</th>
              <th className="data-table__end w-32">Spend</th>
              <th className="data-table__end hidden w-40 @[36rem]:table-cell">Subscription value</th>
              {seats ? <th className="data-table__end hidden w-28 @[44rem]:table-cell">Seats</th> : null}
            </tr>
          </thead>
          <tbody>
            {lines.flatMap((l) => [
              <tr key={l.areaId || '__none'}>
                <td>
                  <span className={l.areaId ? 'font-medium' : 'text-muted'}>{l.name}</span>
                  {l.costCode ? <span className="data-table__meta">{l.costCode}</span> : null}
                </td>
                <td className="data-table__end">
                  {usd(l.usd)}
                  {/* Narrow, the hidden columns' figures go under the spend, when there is one to show. */}
                  {(l.subscriptionUsd ?? 0) > 0 ? <span className="data-table__meta @[36rem]:hidden">{usd(l.subscriptionUsd ?? 0)} subscription</span> : null}
                  {seats && (l.seatUsd ?? 0) > 0 ? <span className="data-table__meta @[44rem]:hidden">{usd(l.seatUsd ?? 0)} seats</span> : null}
                </td>
                <td className="data-table__end hidden @[36rem]:table-cell">{usd(l.subscriptionUsd ?? 0)}</td>
                {seats ? <td className="data-table__end hidden @[44rem]:table-cell">{usd(l.seatUsd ?? 0)}</td> : null}
              </tr>,
              ...l.projects.map((p) => (
                <tr key={`${l.areaId}/${p.projectId}`} className="text-muted">
                  <td className="ps-8">
                    <Link to={`/projects/${encodeURIComponent(p.projectId)}`} className="hover:underline">
                      {p.name}
                    </Link>
                    {p.costCode && p.costCode !== l.costCode ? <span className="data-table__meta">{p.costCode}</span> : null}
                  </td>
                  <td className="data-table__end">{usd(p.usd)}</td>
                  <td className="data-table__end hidden @[36rem]:table-cell">{usd(p.subscriptionUsd ?? 0)}</td>
                  {seats ? <td className="data-table__end hidden @[44rem]:table-cell">{usd(p.seatUsd ?? 0)}</td> : null}
                </tr>
              )),
            ])}
          </tbody>
        </table>
      </div>
    </ListBlock>
  );
}

/** The month's seats per vendor: count times the monthly cost, what calls on them shared out, and what nobody used. */
function SeatLines({ seats, total, usd }: { seats: ChargebackSeatLine[]; total: number; usd: (n: number, digits?: number) => string }) {
  return (
    <ListGroup title="Subscription seats" count={seats.length} description={`${usd(total)} a fixed cost, not in the spend above.`}>
      {seats.map((v) => (
        <ListRow
          key={v.vendor}
          title={VENDOR_WORDS[v.vendor] ?? v.vendor}
          description={
            <>
              {v.note ? `${v.note} · ` : ''}
              {v.count} {v.count === 1 ? 'seat' : 'seats'} at {usd(v.monthlyUsd)} a month ·{' '}
              {v.calls === 0
                ? 'no calls on these seats this month, so none of it is shared out'
                : `shared by ${v.calls.toLocaleString()} ${v.calls === 1 ? 'call' : 'calls'} on these seats${v.unallocatedUsd > 0 ? `; ${usd(v.unallocatedUsd)} not used by any project` : ''}`}
            </>
          }
          value={<span className="tabular-nums text-foreground">{usd(v.usd)}</span>}
        />
      ))}
    </ListGroup>
  );
}

/** A closed month's invoices beside the ledger (cost round 4): the variance as a dot and a word. */
function InvoiceLines({ invoices, usd }: { invoices: ChargebackInvoiceLine[]; usd: (n: number, digits?: number) => string }) {
  return (
    <ListGroup title="Invoices on the statement" count={invoices.length} description="What the providers billed, beside the ledger's metered spend.">
      {invoices.map((i) => (
        <ListRow
          key={i.invoiceId}
          title={invoiceProviderWords(i.provider)}
          description={<span className="tabular-nums">{usd(i.total)}</span>}
          status={<StatusMark state={i.status === 'over' ? 'error' : i.status === 'under' ? 'medium' : 'connected'} label={Math.abs(i.variance) < 0.005 ? 'Matches the ledger' : `${usd(Math.abs(i.variance))} ${i.variance > 0 ? 'over' : 'under'}`} />}
        />
      ))}
    </ListGroup>
  );
}

/** A month as its name and year ("October 2026"), not its first day. */
const monthName = (m: string) => monthWords(m);
